import { useEffect, useState, useCallback } from "react";
import api, { formatApiError, gbp } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

const ALL = "__ALL__";

const TENDER_FIELDS = [
  { key: "account", label: "Account" },
  { key: "cash", label: "Cash" },
  { key: "deliveroo", label: "Deliveroo" },
  { key: "just_eat_card", label: "Just Eat Card" },
  { key: "paypal", label: "PayPal" },
  { key: "pdq", label: "PDQ" },
  { key: "uber_eats", label: "Uber Eats" },
  { key: "webcard", label: "Webcard" },
];

export default function AdminSales() {
  const [stores, setStores] = useState([]);
  const [filters, setFilters] = useState({ start_date: "", end_date: "", store_id: ALL });
  const [data, setData] = useState({ sales: [], totals: { net_sales: 0, tendered_total: 0 } });
  const [loading, setLoading] = useState(false);

  const loadStores = useCallback(async () => {
    try { const { data } = await api.get("/stores"); setStores(data); } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.store_id && filters.store_id !== ALL) params.store_id = filters.store_id;
      const { data } = await api.get("/sales", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { loadStores(); }, [loadStores]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this sales entry?")) return;
    try { await api.delete(`/sales/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const reset = () => setFilters({ start_date: "", end_date: "", store_id: ALL });

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="label-uc">Reporting</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Sales</h1>
          <p className="text-sm text-zinc-500 mt-1">Net sales and tender breakdown across all stores.</p>
        </div>
        <Button variant="outline" onClick={reset} className="rounded-none border-zinc-300" data-testid="sales-filters-reset">Reset</Button>
      </div>

      <div className="bg-white border border-zinc-200 p-4 grid md:grid-cols-3 gap-3 mb-6" data-testid="sales-filters-bar">
        <div>
          <Label className="label-uc">From</Label>
          <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
            className="rounded-none border-zinc-300 mt-1.5 font-mono-data" data-testid="sales-filter-start" />
        </div>
        <div>
          <Label className="label-uc">To</Label>
          <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
            className="rounded-none border-zinc-300 mt-1.5 font-mono-data" data-testid="sales-filter-end" />
        </div>
        <div>
          <Label className="label-uc">Store</Label>
          <Select value={filters.store_id} onValueChange={(v) => setFilters({ ...filters, store_id: v })}>
            <SelectTrigger className="rounded-none border-zinc-300 mt-1.5" data-testid="sales-filter-store">
              <SelectValue placeholder="All stores" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All stores</SelectItem>
              {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Top stats */}
      <div className="grid md:grid-cols-3 gap-0 border border-zinc-200 bg-white mb-6" data-testid="sales-top-stats">
        <div className="p-4">
          <div className="label-uc">Net sales</div>
          <div className="mt-1 text-2xl font-bold font-mono-data" data-testid="stat-net-sales">{gbp(data.totals.net_sales)}</div>
        </div>
        <div className="p-4 border-l border-zinc-200">
          <div className="label-uc">Tendered total</div>
          <div className="mt-1 text-2xl font-bold font-mono-data" data-testid="stat-tendered-total">{gbp(data.totals.tendered_total)}</div>
        </div>
        <div className="p-4 border-l border-zinc-200">
          <div className="label-uc">Variance</div>
          <div className={`mt-1 text-2xl font-bold font-mono-data ${Math.abs(data.totals.tendered_total - data.totals.net_sales) < 0.01 ? "text-emerald-700" : "text-[#DF1A22]"}`} data-testid="stat-variance">
            {gbp(data.totals.tendered_total - data.totals.net_sales)}
          </div>
        </div>
      </div>

      {/* Tender breakdown stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-zinc-200 bg-white mb-6" data-testid="sales-tender-stats">
        {TENDER_FIELDS.map((f, i) => (
          <div key={f.key} className={`p-4 ${i % 4 !== 0 ? "border-l border-zinc-200" : ""} ${i >= 4 ? "border-t border-zinc-200" : ""}`}>
            <div className="label-uc">{f.label}</div>
            <div className="mt-1 text-base font-semibold font-mono-data" data-testid={`stat-${f.key}`}>{gbp(data.totals[f.key])}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-sales-table">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              <th className="px-3 py-2 label-uc">Date</th>
              <th className="px-3 py-2 label-uc">Store</th>
              <th className="px-3 py-2 label-uc text-right">Net</th>
              <th className="px-3 py-2 label-uc text-right">Tendered</th>
              {TENDER_FIELDS.map((f) => (
                <th key={f.key} className="px-3 py-2 label-uc text-right">{f.label.split(" ")[0]}</th>
              ))}
              <th className="px-3 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={13} className="px-3 py-12 text-center text-zinc-400">Loading…</td></tr>}
            {!loading && data.sales.length === 0 && (
              <tr><td colSpan={13} className="px-3 py-12 text-center text-zinc-400">No sales entries.</td></tr>
            )}
            {!loading && data.sales.map((r) => (
              <tr key={r.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`admin-sales-row-${r.id}`}>
                <td className="px-3 py-2 font-mono-data">{r.date}</td>
                <td className="px-3 py-2">{r.store_name}</td>
                <td className="px-3 py-2 text-right font-mono-data font-semibold">{gbp(r.net_sales)}</td>
                <td className="px-3 py-2 text-right font-mono-data">{gbp(r.tendered_total)}</td>
                {TENDER_FIELDS.map((f) => (
                  <td key={f.key} className="px-3 py-2 text-right font-mono-data text-zinc-600">{gbp(r[f.key])}</td>
                ))}
                <td className="px-3 py-2 text-right">
                  <button onClick={() => remove(r.id)} className="text-zinc-400 hover:text-[#DF1A22]"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
